import {
  cyan,
  FaroButton,
  FaroChipList,
  FaroChipTag,
  FaroRichTextViewer,
  FaroText,
  FileDetailsList,
  FontWeights,
  grey,
  NO_TRANSLATE_CLASS,
  NoTranslate,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { convertToDateString, parseUrlLikeString } from "@faro-lotv/foundation";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import { PropsWithChildren, useMemo, useState, type JSX } from "react";
import { UserTeamAvatar } from "../../users-invite/user-team-avater";
import { AnnotationViewerHeader } from "./annotation-viewer-header";
import type {
  AnnotationViewerProps,
  ExternalAnnotationViewerSpecificProps,
  Tag,
} from "./annotation-viewer-types";

const DEFAULT_BACKGROUND = `${grey[1000]}E6`;

/**
 * @returns Information of an annotation
 *  This component has three possible state/variants:
 *   - collapsed -> the annotation ui is hidden
 *   - title -> only the title will be shown
 *   - small -> title, description and some control buttons will be shown
 *   - full -> On top of what small shows, also assignee, due date, status and the sync service will be shown
 */
export function AnnotationViewer({
  children,
  title,
  description,
  hasAttachment,
  isMeasurement,
  createdBy,
  creationDate,
  assignee,
  canEdit,
  canDelete,
  dueDate,
  status,
  externalAnnotationViewerProps,
  variant,
  files,
  links,
  tags,
  size,
  isLoading,
  onCloseButtonClick,
  onDelete,
  onEdit,
  onAnnotationBoxClick,
  onShowMoreButtonClick,
  onShareButtonClick,
  onCopyToClipboard,
  onContextMenuOpen,
  onGoToButtonClick,
  onAttachmentOpened,
  onDescriptionError,
  markupCoordinateString,
  onCopyMarkupCoordinates,
}: PropsWithChildren<AnnotationViewerProps>): JSX.Element {
  const sx: SxProps<Theme> = useMemo(() => {
    switch (variant) {
      case "collapsed":
        return {
          width: 0,
          height: 0,
          overflow: "hidden",
        };
      case "title":
        return {
          backgroundColor: DEFAULT_BACKGROUND,
          maxWidth: "250px",
          // Make sure the title and the icon are at the center of the card
          alignContent: "center",
          "& .MuiCardHeader-avatar": {
            alignSelf: "flex-start",
            marginRight: 1,
          },
          "& .MuiCardHeader-title": {
            py: 0.3,
            display: "flex",
          },
          backdropFilter: "blur(4px)",
        };
      default:
        return {
          cursor: variant === "small" ? "pointer" : "default",
          overflow: "auto",
          backgroundColor: DEFAULT_BACKGROUND,
          p: 1.5,
          ...(size && {
            minWidth: "min-content",
            width: `${size.width}px`,
            maxHeight: `${size.height}px`,
          }),
          "& .MuiCardHeader-root": {
            // Remove extra padding from the header
            p: 0,
            // Add space between header and content
            pb: 1,
          },
          "& .MuiCardHeader-avatar": {
            alignSelf: "flex-start",
          },
          backdropFilter: "blur(4px)",

          // Scrollbar styles
          scrollbarWidth: "thin",
          ["&::-webkit-scrollbar-thumb"]: {
            background: grey[800],
            ["&:hover"]: {
              background: grey[500],
            },
          },
          scrollbarColor: `${grey[600]} ${grey[800]}`,
        };
    }
  }, [size, variant]);

  const shouldShowTags =
    !!tags?.length && (variant === "small" || variant === "full");

  const hideDescriptionLabel = variant !== "full";

  return (
    <Card
      variant="outlined"
      sx={sx}
      onPointerDown={(e) => {
        e.stopPropagation();
        onAnnotationBoxClick?.();
      }}
      onWheel={(e) => e.stopPropagation()}
    >
      <AnnotationViewerHeader
        canEdit={canEdit}
        canDelete={canDelete}
        hasAttachment={hasAttachment}
        links={links}
        isMeasurement={isMeasurement}
        title={title}
        size={size}
        variant={variant}
        isLoading={isLoading}
        externalAnnotationViewerProps={externalAnnotationViewerProps}
        onCloseButtonClick={onCloseButtonClick}
        onDelete={onDelete}
        onEdit={onEdit}
        onShareButtonClick={onShareButtonClick}
        onGoToButtonClick={onGoToButtonClick}
        onCopyToClipboard={onCopyToClipboard}
        onContextMenuOpen={onContextMenuOpen}
        markupCoordinateString={markupCoordinateString}
        onCopyMarkupCoordinates={onCopyMarkupCoordinates}
      />
      <CardContent
        sx={{
          p: 0,
          ":last-child": {
            p: 0,
          },
          ...(variant === "title" && {
            visibility: "collapse",
          }),
          maxWidth: "30vw",
        }}
      >
        {shouldShowTags && <AnnotationTagChips tags={tags} sx={{ pb: 1.5 }} />}

        {variant === "small" && (
          <>
            {children}

            {description && (
              // Add support for eliding in https://faro01.atlassian.net/browse/SWEB-4856
              <ShortDescriptionField
                hasMoreFields={
                  !!assignee ||
                  !!dueDate ||
                  !!status ||
                  !!externalAnnotationViewerProps
                }
                hideDescriptionLabel={hideDescriptionLabel}
                onShowMoreButtonClick={onShowMoreButtonClick}
                onDescriptionError={onDescriptionError}
              >
                {description}
              </ShortDescriptionField>
            )}
          </>
        )}

        {variant === "full" && (
          <FullAnnotationViewerBody
            description={description}
            links={links}
            files={files}
            onAttachmentOpened={onAttachmentOpened}
            dueDate={dueDate}
            assignee={assignee}
            status={status}
            externalAnnotationViewerProps={externalAnnotationViewerProps}
            createdBy={createdBy}
            creationDate={creationDate}
            onDescriptionError={onDescriptionError}
          >
            {children}
          </FullAnnotationViewerBody>
        )}
      </CardContent>
    </Card>
  );
}

type FullAnnotationViewerBodyProps = Pick<
  AnnotationViewerProps,
  | "description"
  | "links"
  | "files"
  | "onAttachmentOpened"
  | "dueDate"
  | "assignee"
  | "status"
  | "externalAnnotationViewerProps"
  | "createdBy"
  | "creationDate"
  | "onDescriptionError"
>;

function FullAnnotationViewerBody({
  children,
  description,
  links,
  files,
  onAttachmentOpened,
  dueDate,
  assignee,
  status,
  externalAnnotationViewerProps,
  createdBy,
  creationDate,
  onDescriptionError,
}: PropsWithChildren<FullAnnotationViewerBodyProps>): JSX.Element {
  const fields = [
    ...(assignee
      ? [
          {
            name: "Assignee",
            value: (
              <UserTeamAvatar
                userDisplayInfo={assignee}
                size="xs"
                shouldShowWhiteRim={false}
                displayOption="nameOnly"
                dark
                isTeam={assignee.isTeam}
              />
            ),
          },
        ]
      : []),
    ...(dueDate
      ? [
          {
            name: "Due date",
            value: convertToDateString(dueDate.toString()),
          },
        ]
      : []),
    ...(status ? [{ name: "Status", value: status }] : []),
    ...(externalAnnotationViewerProps
      ? [
          {
            name: "Annotation type",
            value: `${externalAnnotationViewerProps.providerName} ${externalAnnotationViewerProps.annotationTypeName}`,
          },
          ...externalAnnotationViewerProps.fields,
        ]
      : []),
  ];

  const providerLink = externalAnnotationViewerProps?.link;

  return (
    <Stack sx={{ gap: 1.5 }}>
      {children}
      {description && (
        <FaroRichTextViewer
          initialText={description}
          dark
          label="Description"
          textSize="s"
          onError={onDescriptionError}
        />
      )}
      {links && <LinksList links={links} />}
      {fields.length > 0 && (
        <Grid container sx={{ rowGap: 1.5 }}>
          {fields.map((field) => (
            <Grid key={field.name} size={6}>
              <AnnotationField fieldName={field.name}>
                {field.value}
              </AnnotationField>
            </Grid>
          ))}
        </Grid>
      )}
      {files && files.length > 0 && (
        <FileDetailsList
          dark
          sx={{ p: 0 }}
          files={files}
          onAttachmentOpened={onAttachmentOpened}
        />
      )}
      {providerLink && (
        <ExternalAnnotationLink
          {...externalAnnotationViewerProps}
          link={providerLink}
        />
      )}
      {createdBy?.name && (
        <FaroText variant="bodyS" dark>
          <b>Created by</b>{" "}
          <NoTranslate>
            {createdBy.name}
            {creationDate
              ? `, ${convertToDateString(creationDate.toISOString())}`
              : undefined}
          </NoTranslate>
        </FaroText>
      )}
    </Stack>
  );
}

type ShortDescriptionFieldProps = Pick<
  AnnotationViewerProps,
  "onDescriptionError"
> & {
  /** The description to show while hovering */
  children: AnnotationViewerProps["description"];

  /** True if the annotations has more fields to show */
  hasMoreFields: boolean;

  /**
   * Flag to hide the description label
   *
   * @default false
   */
  hideDescriptionLabel?: boolean;

  /** The user clicked on the Show more... button */
  onShowMoreButtonClick: AnnotationViewerProps["onShowMoreButtonClick"];
};

function ShortDescriptionField({
  children,
  hasMoreFields,
  hideDescriptionLabel = false,
  onShowMoreButtonClick,
  onDescriptionError,
}: ShortDescriptionFieldProps): JSX.Element {
  const [isTextShortened, setIsTextShortened] = useState(false);

  return (
    <Stack>
      <FaroRichTextViewer
        initialText={children ?? ""}
        maxLength={250}
        onTextShortened={() => setIsTextShortened(true)}
        dark
        label={hideDescriptionLabel ? undefined : "Description"}
        textSize="s"
        onError={onDescriptionError}
      />

      {(isTextShortened || hasMoreFields) && (
        <FaroButton
          size="s"
          variant="ghost"
          dark
          onClick={onShowMoreButtonClick}
        >
          Show more
        </FaroButton>
      )}
    </Stack>
  );
}

interface AnnotationTagChipsProps {
  /** The tags of the annotation */
  tags?: Tag[];

  /** Custom styles to apply */
  sx?: SxProps<Theme>;
}

function AnnotationTagChips({
  tags = [],
  sx,
}: AnnotationTagChipsProps): JSX.Element {
  return (
    <FaroChipList
      chips={tags.map((t) => (
        <FaroChipTag key={t.id} label={t.label} color={t.color} dark />
      ))}
      dark
      sx={sx}
    />
  );
}

type AnnotationFieldProps = {
  /** The label to render for the field */
  fieldName: string;

  /** Custom styles to apply */
  sx?: SxProps<Theme>;

  /** adds a max-height to the content and enables the scrolling */
  enableScroll?: boolean;
};

/**
 * @returns A value together with a label
 */
function AnnotationField({
  sx,
  fieldName,
  enableScroll,
  children,
}: PropsWithChildren<AnnotationFieldProps>): JSX.Element {
  return (
    <Stack sx={sx}>
      <FaroText
        variant="labelM"
        sx={{ color: grey[300], pb: 1, fontWeight: FontWeights.Bold }}
      >
        {fieldName}
      </FaroText>
      <FaroText
        variant="bodyS"
        onWheel={(e) => e.stopPropagation()}
        className={NO_TRANSLATE_CLASS}
        sx={{
          color: grey[0],
          ...(enableScroll && {
            maxHeight: "20vh",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            scrollbarWidth: "thin",
            ["&::-webkit-scrollbar-thumb"]: {
              background: `${grey[1000]}CC`,
              ["&:hover"]: {
                background: `${grey[500]}CC`,
              },
            },
            scrollbarColor: `${grey[400]}CC ${grey[1000]}CC`,
          }),
        }}
      >
        {children ?? "-"}
      </FaroText>
    </Stack>
  );
}

type LinksListProps = Pick<AnnotationViewerProps, "links">;

function LinksList({ links }: LinksListProps): JSX.Element | null {
  if (!links) return null;
  return (
    <Stack sx={{ width: "100%" }}>
      <FaroText
        variant="labelM"
        sx={{ color: grey[300], fontWeight: FontWeights.Bold }}
      >
        Links
      </FaroText>
      {links.map((link) => (
        <Link
          className={NO_TRANSLATE_CLASS}
          key={link.href}
          target="_blank"
          sx={{ color: cyan[400], fontSize: "0.75rem" }}
          href={parseUrlLikeString(link.href)}
          title={`Open Link: ${link.href}`}
        >
          {link.label}
        </Link>
      ))}
    </Stack>
  );
}

/**
 * @returns A link to open the annotation in the provider's platform
 */
function ExternalAnnotationLink({
  providerName,
  link,
  logo,
}: Exclude<
  ExternalAnnotationViewerSpecificProps,
  "annotationTypeName" | "link"
> & {
  link: string;
}): JSX.Element {
  return (
    <Link
      target="_blank"
      sx={{ color: cyan[400], fontSize: "0.75rem" }}
      rel="noopener noreferrer"
      href={link}
      underline="none"
      title={`Open Link: ${link}`}
    >
      <Stack direction="row">
        <Box component="div" sx={{ py: 1 }}>
          {logo}
        </Box>
        <FaroText
          variant="labelM"
          sx={{
            color: grey[0],
            py: 1,
            ml: 0.5,
            fontWeight: FontWeights.Bold,
          }}
        >
          Open in{" "}
          <TranslateVar name="providerName">{providerName}</TranslateVar>
        </FaroText>
      </Stack>
    </Link>
  );
}
